/* eslint-disable camelcase */
import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import AmplifyApiService from '../../services/AmplifyApiService';
import IAmplifyApiConfig from '../../shared/interfaces/IAmplifyApiConfig';

const amplifyApiService = new AmplifyApiService();

const BASE_PATH = 'permissions';

export enum PermissionStatusType {
  ACTIVE = 'active',
  WITHDRAWN = 'withdrawn',
}

export interface IPermission {
  id: number;
  bpn: string;
  companyName: string;
  companyId: number;
  dateCreated: string;
  createdBy: {
    id: number;
    firstName: string;
    lastName: string;
  };
  productId: number;
  status: PermissionStatusType;
  canDeleteAccess: boolean;
}

export interface CreatePermissionRequest {
  bpn: string;
  productId: number;
}

interface DeletePermissionProps {
  permissionId: number;
}

interface WithdrawPermissionProps {
  permissionId: number;
}

interface CheckPermissionLatestPcfResponse {
  hasLatestPcf: boolean;
}

interface ReactivatePermissionProps {
  permissionId: number;
  shareLatestPCf: boolean;
}

export const useListPermissions = (
  productId: string,
  options: UseQueryOptions<IPermission[], Error> = {},
): UseQueryResult<IPermission[]> => {
  const amplifyApiConfig: IAmplifyApiConfig = {
    apiName: 'PacificApi',
    path: BASE_PATH,
    options: {
      queryParams: {
        productId,
      },
    },
  };

  const query = useQuery<IPermission[], Error>({
    queryKey: ['permissions', productId],
    queryFn: async (): Promise<IPermission[]> => {
      const response = await amplifyApiService.get<IPermission[]>(amplifyApiConfig);

      return response;
    },
    ...options,
  });

  return query;
};

export const useCreatePermission = (
  options: UseMutationOptions<IPermission, Error, CreatePermissionRequest> = {},
): UseMutationResult<IPermission, Error, CreatePermissionRequest> => {
  const mutation = useMutation<IPermission, Error, CreatePermissionRequest>({
    mutationFn: async (body: CreatePermissionRequest) => {
      const amplifyApiConfig: IAmplifyApiConfig = {
        apiName: 'PacificApi',
        path: BASE_PATH,
        options: { body },
      };

      return await amplifyApiService.post<CreatePermissionRequest, IPermission>(amplifyApiConfig);
    },
    ...options,
  });

  return mutation;
};

export const useDeletePermission = (
  options: UseMutationOptions<void, Error, DeletePermissionProps> = {},
): UseMutationResult<void, Error, DeletePermissionProps> => {
  const mutation = useMutation<void, Error, DeletePermissionProps>({
    mutationFn: async ({ permissionId }: DeletePermissionProps) => {
      const amplifyApiConfig: IAmplifyApiConfig = {
        apiName: 'PacificApi',
        path: `${BASE_PATH}/${permissionId}`,
        options: {},
      };

      return await amplifyApiService.delete<undefined, void>(amplifyApiConfig);
    },
    ...options,
  });

  return mutation;
};

export const useWithdrawPermission = (
  options: UseMutationOptions<void, Error, WithdrawPermissionProps> = {},
): UseMutationResult<void, Error, WithdrawPermissionProps> => {
  const mutation = useMutation<void, Error, WithdrawPermissionProps>({
    mutationFn: async ({ permissionId }: WithdrawPermissionProps) => {
      const amplifyApiConfig: IAmplifyApiConfig = {
        apiName: 'PacificApi',
        path: `${BASE_PATH}/${permissionId}/withdraw-permission`,
        options: {},
      };

      return await amplifyApiService.patch<undefined, void>(amplifyApiConfig);
    },
    ...options,
  });

  return mutation;
};

export const useCheckPermissionLatestPcf = (
  permissionId?: number,
  options: UseQueryOptions<CheckPermissionLatestPcfResponse, Error> = {},
): UseQueryResult<CheckPermissionLatestPcfResponse> => {
  const amplifyApiConfig: IAmplifyApiConfig = {
    apiName: 'PacificApi',
    path: `${BASE_PATH}/${permissionId}/latest-pcf`,
  };

  const query = useQuery<CheckPermissionLatestPcfResponse, Error>({
    queryKey: ['check-permission-latest-pcf', permissionId],
    queryFn: async (): Promise<CheckPermissionLatestPcfResponse> => {
      const response = await amplifyApiService.get<CheckPermissionLatestPcfResponse>(
        amplifyApiConfig,
      );

      return response;
    },
    ...options,
  });

  return query;
};

export const useReactivatePermission = (
  options: UseMutationOptions<void, Error, ReactivatePermissionProps> = {},
): UseMutationResult<void, Error, ReactivatePermissionProps> => {
  const mutation = useMutation<void, Error, ReactivatePermissionProps>({
    mutationFn: async ({ permissionId, shareLatestPCf }: ReactivatePermissionProps) => {
      const amplifyApiConfig: IAmplifyApiConfig = {
        apiName: 'PacificApi',
        path: `${BASE_PATH}/${permissionId}/reactivate-permission`,
        options: {
          body: {
            shareLatestPCf,
          },
        },
      };

      return await amplifyApiService.patch<Partial<ReactivatePermissionProps>, void>(
        amplifyApiConfig,
      );
    },
    ...options,
  });

  return mutation;
};
